import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  SunIcon,
  BeakerIcon,
  BugAntIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { format, addDays } from 'date-fns';
import { farmApi, calendarApi, GrowthStage, CalendarActivity, FarmCalendar } from '../lib/api';
import toast from 'react-hot-toast';

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  planting: SunIcon,
  fertilization: BeakerIcon,
  pest_control: BugAntIcon,
  irrigation: SparklesIcon,
  harvest: FlagIcon,
};

const CATEGORY_COLORS: Record<string, string> = {
  planting: 'bg-green-100 text-green-800',
  fertilization: 'bg-purple-100 text-purple-800',
  pest_control: 'bg-orange-100 text-orange-800',
  irrigation: 'bg-blue-100 text-blue-800',
  harvest: 'bg-amber-100 text-amber-800',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

function StageProgressBar({ stages, currentDay }: { stages: GrowthStage[]; currentDay: number }) {
  const totalDuration = stages.reduce((sum, s) => sum + s.duration, 0);
  const progress = Math.min(100, (currentDay / totalDuration) * 100);

  return (
    <div className="relative">
      {/* Progress Track */}
      <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Stage Markers */}
      <div className="relative mt-2">
        {stages.map((stage) => {
          const startPercent = (stage.startDay / totalDuration) * 100;
          const isActive = currentDay >= stage.startDay && currentDay < stage.startDay + stage.duration;
          const isPast = currentDay >= stage.startDay + stage.duration;

          return (
            <div
              key={stage.name}
              className="absolute transform -translate-x-1/2"
              style={{ left: `${startPercent}%` }}
            >
              <div
                className={`w-3 h-3 rounded-full border-2 ${
                  isPast
                    ? 'bg-green-500 border-green-500'
                    : isActive
                    ? 'bg-green-500 border-green-300 ring-2 ring-green-200'
                    : 'bg-white border-gray-300'
                }`}
              />
              <p
                className={`text-xs mt-1 whitespace-nowrap ${
                  isActive ? 'font-medium text-green-700' : 'text-gray-500'
                }`}
                style={{ fontSize: '10px' }}
              >
                {stage.name.split('_').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ActivityCard({ activity, scheduledDate, daysUntil, isOverdue }: {
  activity: CalendarActivity;
  scheduledDate?: string;
  daysUntil?: number;
  isOverdue?: boolean;
}) {
  const Icon = CATEGORY_ICONS[activity.category] || CalendarDaysIcon;

  return (
    <div className={`bg-white rounded-lg border p-4 ${isOverdue ? 'border-red-300 bg-red-50' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3">
          <div className={`p-2 rounded-lg ${CATEGORY_COLORS[activity.category] || 'bg-gray-100'}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">{activity.name}</h4>
            <p className="text-sm text-gray-500">{activity.description}</p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded text-xs font-medium ${PRIORITY_COLORS[activity.priority]}`}>
          {activity.priority}
        </span>
      </div>

      {scheduledDate && (
        <div className="mt-3 flex items-center text-sm">
          <CalendarDaysIcon className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-gray-600">
            {format(new Date(scheduledDate), 'MMM d, yyyy')}
          </span>
          {daysUntil !== undefined && (
            <span className={`ml-2 ${daysUntil < 0 ? 'text-red-600' : daysUntil <= 3 ? 'text-orange-600' : 'text-green-600'}`}>
              ({daysUntil < 0 ? `${Math.abs(daysUntil)} days overdue` : daysUntil === 0 ? 'Today' : `in ${daysUntil} days`})
            </span>
          )}
        </div>
      )}

      {activity.tips.length > 0 && (
        <div className="mt-3 bg-gray-50 rounded p-2">
          <p className="text-xs text-gray-600">
            <strong>Tip:</strong> {activity.tips[0]}
          </p>
        </div>
      )}

      {isOverdue && (
        <div className="mt-2 flex items-center text-red-600 text-sm">
          <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
          Overdue - action required
        </div>
      )}
    </div>
  );
}

function StageCard({ stage, isActive, isPast }: {
  stage: GrowthStage;
  isActive: boolean;
  isPast: boolean;
}) {
  return (
    <div className={`rounded-lg border p-4 ${
      isActive ? 'bg-green-50 border-green-300 ring-2 ring-green-200' :
      isPast ? 'bg-gray-50 border-gray-200' : 'bg-white'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <h4 className={`font-medium ${isActive ? 'text-green-800' : 'text-gray-900'}`}>
          {stage.name.split('_').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
        </h4>
        {isActive && (
          <span className="px-2 py-1 bg-green-600 text-white text-xs rounded-full">
            Current
          </span>
        )}
        {isPast && (
          <CheckCircleIcon className="h-5 w-5 text-green-500" />
        )}
      </div>
      <p className="text-sm text-gray-600 mb-3">{stage.description}</p>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <p className="text-gray-500">Duration</p>
          <p className="font-medium">{stage.duration} days</p>
        </div>
        <div>
          <p className="text-gray-500">Start Day</p>
          <p className="font-medium">Day {stage.startDay}</p>
        </div>
        <div>
          <p className="text-gray-500">Water Needs</p>
          <p className="font-medium capitalize">{stage.waterNeeds}</p>
        </div>
        <div>
          <p className="text-gray-500">Nutrient Needs</p>
          <p className="font-medium capitalize">{stage.nutrientNeeds}</p>
        </div>
      </div>
      {stage.keyIndicators.length > 0 && (
        <div className="mt-3">
          <p className="text-xs text-gray-500 mb-1">Key Indicators:</p>
          <div className="flex flex-wrap gap-1">
            {stage.keyIndicators.map((indicator, i) => (
              <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                {indicator}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function CropCalendar() {
  const [selectedFarm, setSelectedFarm] = useState<string>('');
  const [plantingDate, setPlantingDate] = useState<string>(format(addDays(new Date(), -45), 'yyyy-MM-dd'));
  const [farmCalendar, setFarmCalendar] = useState<FarmCalendar | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'stages' | 'activities'>('overview');

  const { data: farms = [], isLoading: farmsLoading } = useQuery({
    queryKey: ['farms'],
    queryFn: farmApi.list,
  });

  const { data: availableCrops } = useQuery({
    queryKey: ['calendar-crops'],
    queryFn: calendarApi.getAvailableCrops,
  });

  const calendarMutation = useMutation({
    mutationFn: calendarApi.generateFarmCalendar,
    onSuccess: (response) => {
      setFarmCalendar(response.data);
      toast.success('Calendar generated!');
    },
    onError: () => {
      toast.error('Failed to generate calendar');
    },
  });

  const handleGenerate = () => {
    const farm = farms.find(f => f.id === selectedFarm);
    if (!farm) {
      toast.error('Please select a farm');
      return;
    }

    calendarMutation.mutate({
      farmId: selectedFarm,
      cropType: farm.cropType || 'corn',
      plantingDate,
    });
  };

  const selectedFarmData = farms.find(f => f.id === selectedFarm);

  if (farmsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Crop Calendar</h1>
        <p className="text-gray-500">Growth stage tracking and activity management</p>
      </div>

      {/* Configuration */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Farm Calendar</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Farm
            </label>
            <select
              value={selectedFarm}
              onChange={(e) => setSelectedFarm(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            >
              <option value="">Choose a farm...</option>
              {farms.map(farm => (
                <option key={farm.id} value={farm.id}>
                  {farm.name} ({farm.cropType || 'Unknown'})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Planting Date
            </label>
            <input
              type="date"
              value={plantingDate}
              onChange={(e) => setPlantingDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleGenerate}
              disabled={!selectedFarm || calendarMutation.isPending}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center"
            >
              {calendarMutation.isPending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <>
                  <CalendarDaysIcon className="h-5 w-5 mr-2" />
                  Generate Calendar
                </>
              )}
            </button>
          </div>
        </div>

        {availableCrops?.data?.availableCrops && (
          <p className="mt-4 text-sm text-gray-500">
            <strong>Supported crops:</strong> {availableCrops.data.availableCrops.join(', ')}
          </p>
        )}
      </div>

      {/* Calendar Results */}
      {farmCalendar && (
        <div className="space-y-6">
          {/* Progress Overview */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {selectedFarmData?.name} - {farmCalendar.cropType}
                </h3>
                <p className="text-sm text-gray-500">
                  Planted: {format(new Date(farmCalendar.plantingDate), 'MMM d, yyyy')} |
                  Expected Harvest: {format(new Date(farmCalendar.expectedHarvestDate), 'MMM d, yyyy')}
                </p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-green-600">{farmCalendar.progress}%</p>
                <p className="text-sm text-gray-500">Complete</p>
              </div>
            </div>

            {/* Stage Progress Bar */}
            <div className="mb-8">
              <StageProgressBar
                stages={[farmCalendar.currentStage]} // Would need full stages list
                currentDay={farmCalendar.currentDay}
              />
            </div>

            {/* Current Stage Info */}
            <div className="bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Current Stage</p>
                  <p className="text-xl font-bold text-green-800">
                    {farmCalendar.currentStage.name.split('_').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Day {farmCalendar.currentDay} of growing season
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-green-600">Water: {farmCalendar.currentStage.waterNeeds}</p>
                  <p className="text-sm text-green-600">Nutrients: {farmCalendar.currentStage.nutrientNeeds}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b">
            <nav className="flex space-x-8">
              {[
                { id: 'overview', label: 'Overview' },
                { id: 'stages', label: 'Growth Stages' },
                { id: 'activities', label: 'Activities' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upcoming Activities */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2 text-orange-500" />
                  Upcoming Activities
                </h3>
                <div className="space-y-4">
                  {farmCalendar.upcomingActivities.slice(0, 5).map(activity => (
                    <ActivityCard
                      key={activity.id}
                      activity={activity}
                      scheduledDate={activity.scheduledDate}
                      daysUntil={activity.daysUntil}
                    />
                  ))}
                  {farmCalendar.upcomingActivities.length === 0 && (
                    <p className="text-gray-500 text-center py-4">No upcoming activities</p>
                  )}
                </div>
              </div>

              {/* Current Stage Details */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <SunIcon className="h-5 w-5 mr-2 text-yellow-500" />
                  Current Stage Details
                </h3>
                <StageCard
                  stage={farmCalendar.currentStage}
                  isActive={true}
                  isPast={false}
                />
              </div>
            </div>
          )}

          {/* Stages Tab - Would need full stages data */}
          {activeTab === 'stages' && (
            <div className="bg-white rounded-xl shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Growth Stages Timeline</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <StageCard
                  stage={farmCalendar.currentStage}
                  isActive={true}
                  isPast={false}
                />
              </div>
              <p className="text-sm text-gray-500 mt-4">
                Note: View the crop template for complete stage information.
              </p>
            </div>
          )}

          {/* Activities Tab */}
          {activeTab === 'activities' && (
            <div className="space-y-6">
              {/* Upcoming */}
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Activities</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {farmCalendar.upcomingActivities.map(activity => (
                    <ActivityCard
                      key={activity.id}
                      activity={activity}
                      scheduledDate={activity.scheduledDate}
                      daysUntil={activity.daysUntil}
                    />
                  ))}
                </div>
              </div>

              {/* Completed */}
              {farmCalendar.completedActivities.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                    Completed Activities
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {farmCalendar.completedActivities.map(id => (
                      <span key={id} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        {id}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
            <p>
              <strong>Farm ID:</strong> {farmCalendar.farmId} |{' '}
              <strong>Crop:</strong> {farmCalendar.cropType} |{' '}
              <strong>Day:</strong> {farmCalendar.currentDay}
            </p>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!farmCalendar && (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <CalendarDaysIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Calendar Generated</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Select a farm and planting date above to generate a crop calendar with
            growth stages and activity recommendations.
          </p>
        </div>
      )}
    </div>
  );
}
